import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

const STATUSES = ["pending_payment", "queued", "in_progress", "awaiting_review", "revision_requested", "completed"];

export default function Admin() {
  const [orders, setOrders] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [gear, setGear] = useState([]);
  const [tab, setTab] = useState("orders");

  const load = async () => {
    const [o, p, g] = await Promise.all([
      api.get("/admin/orders"), api.get("/portfolio"), api.get("/gear"),
    ]);
    setOrders(o.data); setPortfolio(p.data); setGear(g.data);
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    await api.post("/admin/seed");
    toast.success("Örnek ekipmanlar eklendi");
    load();
  };

  const resetGear = async () => {
    if (!window.confirm("Tüm ekipman listesini silip yeniden yükleyelim mi?")) return;
    await api.post("/admin/reset-gear");
    toast.success("Ekipman listesi güncellendi");
    load();
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16" data-testid="admin-page">
      <div className="mono-caps mb-3 text-amber-400">Admin Studio</div>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Kontrol Paneli</h1>
        <div className="flex gap-2">
          <button onClick={seed} className="btn-ghost" data-testid="seed-btn">Ekipman Örnekleri Yükle</button>
          <button onClick={resetGear} className="btn-ghost" data-testid="reset-gear-btn">Ekipmanı Yeniden Yükle</button>
        </div>
      </div>
      <div className="flex gap-2 mt-8 border-b border-white/10">
        {[
          { id: "orders", l: "Siparişler" },
          { id: "portfolio", l: "Portfolyo" },
          { id: "players", l: "Enstrümanistler" },
          { id: "gear", l: "Ekipman" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-2 mono-caps ${tab === t.id ? "text-cyan-300 border-b-2 border-cyan-400" : "text-slate-400"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "orders" && <OrdersTab orders={orders} reload={load} />}
      {tab === "portfolio" && <PortfolioTab items={portfolio} reload={load} />}
      {tab === "players" && <PlayersTab reload={load} />}
      {tab === "gear" && <GearTab items={gear} reload={load} />}
    </main>
  );
}

function OrdersTab({ orders, reload }) {
  const setStatus = async (id, status) => {
    await api.patch(`/orders/${id}/status`, { status });
    toast.success("Durum güncellendi");
    reload();
  };
  const removeOrder = async (id) => {
    if (!window.confirm(`${id} siparişi kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Sipariş silindi");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme başarısız");
    }
  };
  const upload = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", file.name);
    await api.post(`/orders/${id}/deliverable`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Teslim yüklendi");
    reload();
  };
  return (
    <div className="space-y-6 mt-8">
      {orders.length === 0 && <div className="text-slate-500">Sipariş yok</div>}
      {orders.map((o) => (
        <div key={o.order_id} className="panel p-6" data-testid={`admin-order-${o.order_id}`}>
          <div className="flex justify-between flex-wrap gap-3">
            <div>
              <div className="mono-caps">{o.order_id} · {o.user_email}</div>
              <div className="font-display text-xl text-white">{o.track_name}</div>
              <div className="text-sm text-slate-400">{o.package_name}{o.with_master ? " + Mastering" : ""} · {o.channels} kanal</div>
            </div>
            <div className="text-right">
              <div className="mono-caps">{o.payment_status}</div>
              <div className="font-display text-2xl text-white">{o.total_amount.toLocaleString("tr-TR")} ₺</div>
              <button
                onClick={() => removeOrder(o.order_id)}
                data-testid={`del-order-${o.order_id}`}
                className="mt-2 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full border border-rose-500/40 text-rose-300 hover:text-white hover:bg-rose-500/80 hover:border-rose-500 transition"
              >
                <Trash2 className="w-3 h-3" /> Sil
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => setStatus(o.order_id, s)}
                className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border ${o.status === s ? "bg-cyan-400 text-black border-cyan-400" : "text-slate-300 border-white/10 hover:border-cyan-400"}`}
                data-testid={`set-status-${o.order_id}-${s}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <div className="mono-caps mb-2">Ham Dosyalar</div>
              <ul className="text-sm text-slate-300 space-y-1">
                {o.raw_files.map((f) => (
                  <li key={f.file_id}>
                    <a href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-cyan-200">
                      ⬇ {f.original_filename} ({(f.size / 1e6).toFixed(1)} MB)
                    </a>
                  </li>
                ))}
                {o.raw_files.length === 0 && <li className="text-slate-500">Dosya yok</li>}
              </ul>
            </div>
            <div>
              <div className="mono-caps mb-2">Teslim Dosyaları</div>
              <ul className="text-sm text-slate-300 space-y-1">
                {o.deliverable_files.map((f) => (
                  <li key={f.file_id}>
                    <a href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-cyan-200">
                      ⬇ {f.label || f.original_filename}
                    </a>
                  </li>
                ))}
              </ul>
              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer btn-ghost">
                <Upload className="w-4 h-4" /> Teslim Yükle
                <input type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(o.order_id, e.target.files[0])}
                  data-testid={`upload-deliverable-${o.order_id}`} />
              </label>
            </div>
          </div>
          {o.revision_requests.length > 0 && (
            <div className="mt-6">
              <div className="mono-caps mb-2">Revizyon Talepleri</div>
              <ul className="space-y-2 text-sm text-slate-300">
                {o.revision_requests.map((r) => (
                  <li key={r.id} className="border-l-2 border-cyan-400/40 pl-3">
                    <span className={r.paid ? "text-amber-300" : "text-cyan-300"}>{r.paid ? "Ücretli" : "Ücretsiz"}</span> · {new Date(r.requested_at).toLocaleString("tr-TR")}
                    <div>{r.notes}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PortfolioTab({ items, reload }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [genre, setGenre] = useState("");
  const create = async () => {
    if (!title || !artist) return toast.error("Başlık ve sanatçı gerekli");
    await api.post("/portfolio", { title, artist, genre });
    setTitle(""); setArtist(""); setGenre("");
    reload();
  };
  const uploadAudio = async (pid, file, kind) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    await api.post(`/portfolio/${pid}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Yüklendi");
    reload();
  };
  const del = async (pid) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/portfolio/${pid}`);
    reload();
  };
  return (
    <div className="mt-8 space-y-6">
      <div className="panel p-6 grid md:grid-cols-4 gap-3" data-testid="portfolio-form">
        <input placeholder="Başlık" value={title} onChange={(e) => setTitle(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="Sanatçı" value={artist} onChange={(e) => setArtist(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="Tarz" value={genre} onChange={(e) => setGenre(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <button onClick={create} className="btn-cyan" data-testid="add-portfolio-btn">Portfolyo Ekle</button>
      </div>
      {items.map((it) => (
        <div key={it.portfolio_id} className="panel p-6" data-testid={`admin-portfolio-${it.portfolio_id}`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="font-display text-lg text-white">{it.title}</div>
              <div className="text-sm text-slate-400">{it.artist} · {it.genre}</div>
            </div>
            <button onClick={() => del(it.portfolio_id)} className="text-rose-400 hover:text-rose-300 p-2"
              data-testid={`del-portfolio-${it.portfolio_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {["before", "after", "cover"].map((k) => (
              <label key={k} className="btn-ghost cursor-pointer text-center inline-flex items-center justify-center gap-2">
                <Upload className="w-4 h-4" />
                {k === "before" ? "Before Ses" : k === "after" ? "After Ses" : "Kapak"}
                {it[`${k}_path`] && <span className="text-emerald-400">✓</span>}
                <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadAudio(it.portfolio_id, e.target.files[0], k)} />
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function GearTab({ items, reload }) {
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [description, setDescription] = useState("");
  const add = async () => {
    if (!category || !name) return toast.error("Kategori ve isim gerekli");
    await api.post("/gear", { category, name, brand, description });
    setCategory(""); setName(""); setBrand(""); setDescription("");
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/gear/${id}`);
    reload();
  };
  return (
    <div className="mt-8 space-y-6">
      <div className="panel p-6 grid md:grid-cols-5 gap-3">
        <input placeholder="Kategori" value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="İsim" value={name} onChange={(e) => setName(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="Marka" value={brand} onChange={(e) => setBrand(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="Açıklama" value={description} onChange={(e) => setDescription(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <button onClick={add} className="btn-cyan" data-testid="add-gear-btn">Ekle</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((g) => (
          <div key={g.gear_id} className="panel p-4 flex items-start justify-between">
            <div>
              <div className="mono-caps text-amber-400">{g.category}</div>
              <div className="font-display text-white">{g.name}</div>
              <div className="text-sm text-slate-400">{g.brand}</div>
              {g.description && <div className="text-xs text-slate-500 mt-1">{g.description}</div>}
            </div>
            <button onClick={() => del(g.gear_id)} className="text-rose-400 hover:text-rose-300 p-2"
              data-testid={`del-gear-${g.gear_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


function PlayersTab({ reload }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [instrument, setInstrument] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");

  const load = async () => {
    const r = await api.get("/players");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name || !instrument || !price) return toast.error("İsim, enstrüman ve fiyat gerekli");
    await api.post("/players", { name, instrument, price: Number(price), description });
    setName(""); setInstrument(""); setPrice(""); setDescription("");
    load();
  };
  const uploadPhoto = async (pid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/players/${pid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Fotoğraf yüklendi");
    load();
  };
  const uploadSample = async (pid, file, title) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || file.name);
    await api.post(`/players/${pid}/samples`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Örnek yüklendi");
    load();
  };
  const delPlayer = async (pid) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/players/${pid}`);
    load();
  };
  const delSample = async (pid, sid) => {
    if (!window.confirm("Örnek silinsin mi?")) return;
    await api.delete(`/players/${pid}/samples/${sid}`);
    load();
  };

  return (
    <div className="mt-8 space-y-6">
      <div className="panel p-6 grid md:grid-cols-5 gap-3" data-testid="player-form">
        <input placeholder="İsim (müzisyen)" value={name} onChange={(e) => setName(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="Enstrüman" value={instrument} onChange={(e) => setInstrument(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="Fiyat (₺)" type="number" value={price} onChange={(e) => setPrice(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <input placeholder="Kısa açıklama" value={description} onChange={(e) => setDescription(e.target.value)}
          className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
        <button onClick={add} className="btn-cyan" data-testid="add-player-btn">Ekle</button>
      </div>

      {items.map((p) => (
        <div key={p.player_id} className="panel p-6" data-testid={`admin-player-${p.player_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="mono-caps text-amber-400">{p.instrument}</div>
              <div className="font-display text-xl text-white">{p.name}</div>
              <div className="text-sm text-slate-400">{p.description}</div>
              <div className="mt-2 font-display text-lg text-white">{Number(p.price).toLocaleString("tr-TR")} ₺</div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <label className="btn-ghost cursor-pointer inline-flex items-center gap-2">
                <Upload className="w-4 h-4" /> Fotoğraf
                {p.photo_path && <span className="text-emerald-400">✓</span>}
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadPhoto(p.player_id, e.target.files[0])} />
              </label>
              <label className="btn-ghost cursor-pointer inline-flex items-center gap-2">
                <Upload className="w-4 h-4" /> Örnek Ses
                <input type="file" accept="audio/*" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadSample(p.player_id, e.target.files[0])} />
              </label>
              <button onClick={() => delPlayer(p.player_id)} className="text-rose-400 hover:text-rose-300 p-2"
                data-testid={`del-player-${p.player_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          {p.samples?.length > 0 && (
            <ul className="mt-4 space-y-1 text-sm text-slate-300">
              {p.samples.map((s) => (
                <li key={s.sample_id} className="flex items-center gap-2">
                  <span>• {s.title}</span>
                  <button onClick={() => delSample(p.player_id, s.sample_id)} className="ml-auto text-rose-400 hover:text-rose-300">
                    Sil
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
